/**
 * ChatMessage Component
 * Phase 6.5 - Chat Conversationnel (CFO Virtuel)
 */

import React, { useState } from 'react';
import {
  User,
  Bot,
  ThumbsUp,
  ThumbsDown,
  ExternalLink,
  ChevronRight,
  TrendingUp,
  TrendingDown,
  Minus,
} from 'lucide-react';
import type {
  ChatMessage as ChatMessageType,
  ChatMessageRole,
  SuggestedAction,
  ChatResponseSource,
} from '../types/chat.types';

interface ChatMessageProps {
  message: ChatMessageType;
  onFeedback?: (rating: number, comment?: string) => void;
  onActionClick?: (action: SuggestedAction) => void;
  onSourceClick?: (source: ChatResponseSource) => void;
}

export const ChatMessage: React.FC<ChatMessageProps> = ({
  message,
  onFeedback,
  onActionClick,
  onSourceClick,
}) => {
  const [feedbackGiven, setFeedbackGiven] = useState<number | null>(
    message.feedbackRating || null
  );

  const isUser = message.role === 'USER';
  const isAssistant = message.role === 'ASSISTANT';

  const handleFeedback = (rating: number) => {
    if (feedbackGiven !== null) return;
    setFeedbackGiven(rating);
    onFeedback?.(rating);
  };

  // Parse rich content if contentType is RICH or TABLE or CHART
  const renderContent = () => {
    if (message.contentType === 'TEXT') {
      return (
        <p className="text-sm whitespace-pre-wrap leading-relaxed">
          {message.content}
        </p>
      );
    }

    // For rich content, try to parse metadata
    if (message.contentType === 'RICH' && message.metadata) {
      return (
        <div className="space-y-2">
          <p className="text-sm whitespace-pre-wrap leading-relaxed">
            {message.content}
          </p>
          {message.metadata.highlights && (
            <div className="mt-2 space-y-1">
              {message.metadata.highlights.map((highlight: string, idx: number) => (
                <div
                  key={idx}
                  className="flex items-center gap-2 text-xs bg-teal-50 text-teal-700 px-2 py-1 rounded"
                >
                  <ChevronRight className="w-3 h-3" />
                  {highlight}
                </div>
              ))}
            </div>
          )}
        </div>
      );
    }

    if (message.contentType === 'TABLE' && message.metadata?.table) {
      const { headers, rows } = message.metadata.table;
      return (
        <div className="space-y-2">
          <p className="text-sm whitespace-pre-wrap">{message.content}</p>
          <div className="overflow-x-auto">
            <table className="min-w-full text-xs border border-gray-200 rounded">
              <thead className="bg-gray-50">
                <tr>
                  {headers?.map((header: string, idx: number) => (
                    <th key={idx} className="px-2 py-1 text-left font-medium">
                      {header}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows?.map((row: string[], rowIdx: number) => (
                  <tr key={rowIdx} className="border-t border-gray-100">
                    {row.map((cell: string, cellIdx: number) => (
                      <td key={cellIdx} className="px-2 py-1">
                        {cell}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      );
    }

    // Default text rendering
    return (
      <p className="text-sm whitespace-pre-wrap leading-relaxed">
        {message.content}
      </p>
    );
  };

  // Render trend indicator
  const renderTrend = (trend: 'UP' | 'DOWN' | 'STABLE') => {
    switch (trend) {
      case 'UP':
        return <TrendingUp className="w-4 h-4 text-green-500" />;
      case 'DOWN':
        return <TrendingDown className="w-4 h-4 text-red-500" />;
      default:
        return <Minus className="w-4 h-4 text-gray-400" />;
    }
  };

  return (
    <div
      className={`flex gap-3 ${isUser ? 'flex-row-reverse' : ''} animate-fade-in`}
    >
      {/* Avatar */}
      <div
        className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${
          isUser
            ? 'bg-teal-100 text-teal-600'
            : 'bg-gradient-to-br from-teal-500 to-teal-600 text-white'
        }`}
      >
        {isUser ? (
          <User className="w-4 h-4" />
        ) : (
          <Bot className="w-4 h-4" />
        )}
      </div>

      {/* Message Content */}
      <div
        className={`flex-1 max-w-[80%] ${isUser ? 'items-end' : 'items-start'}`}
      >
        <div
          className={`rounded-2xl px-4 py-3 ${
            isUser
              ? 'bg-teal-600 text-white rounded-tr-sm'
              : 'bg-white border border-gray-100 shadow-sm text-gray-800 rounded-tl-sm'
          }`}
        >
          {renderContent()}
        </div>

        {/* Suggested Actions */}
        {isAssistant && message.suggestedActions && message.suggestedActions.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-2">
            {message.suggestedActions.map((action, idx) => (
              <button
                key={idx}
                onClick={() => onActionClick?.(action)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium
                  bg-teal-50 text-teal-700 rounded-full hover:bg-teal-100
                  transition-colors duration-200"
              >
                {action.label}
                <ChevronRight className="w-3 h-3" />
              </button>
            ))}
          </div>
        )}

        {/* Sources */}
        {isAssistant && message.sources && message.sources.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-1.5">
            {message.sources.map((source, idx) => (
              <button
                key={idx}
                onClick={() => onSourceClick?.(source)}
                className="inline-flex items-center gap-1 px-2 py-1 text-xs
                  text-gray-500 hover:text-teal-600 hover:bg-gray-50
                  rounded transition-colors duration-200"
              >
                <ExternalLink className="w-3 h-3" />
                {source.label}
              </button>
            ))}
          </div>
        )}

        {/* Feedback buttons (for assistant messages only) */}
        {isAssistant && onFeedback && (
          <div className="mt-2 flex items-center gap-2">
            <button
              onClick={() => handleFeedback(1)}
              disabled={feedbackGiven !== null}
              className={`p-1.5 rounded-full transition-all duration-200 ${
                feedbackGiven === 1
                  ? 'bg-green-100 text-green-600'
                  : feedbackGiven !== null
                  ? 'opacity-30 cursor-not-allowed text-gray-400'
                  : 'hover:bg-gray-100 text-gray-400 hover:text-green-600'
              }`}
              title="Utile"
            >
              <ThumbsUp className="w-3.5 h-3.5" />
            </button>
            <button
              onClick={() => handleFeedback(-1)}
              disabled={feedbackGiven !== null}
              className={`p-1.5 rounded-full transition-all duration-200 ${
                feedbackGiven === -1
                  ? 'bg-red-100 text-red-600'
                  : feedbackGiven !== null
                  ? 'opacity-30 cursor-not-allowed text-gray-400'
                  : 'hover:bg-gray-100 text-gray-400 hover:text-red-600'
              }`}
              title="Pas utile"
            >
              <ThumbsDown className="w-3.5 h-3.5" />
            </button>
          </div>
        )}

        {/* Timestamp */}
        <div
          className={`mt-1 text-[10px] text-gray-400 ${
            isUser ? 'text-right' : 'text-left'
          }`}
        >
          {new Date(message.createdAt).toLocaleTimeString('fr-FR', {
            hour: '2-digit',
            minute: '2-digit',
          })}
        </div>
      </div>
    </div>
  );
};

export default ChatMessage;
